/**
 * Knowledge capture (#193), client side. A contribution is what someone
 * knows about a number, saved as an annotation of kind `contribution` bound
 * to the metric, the window and the perspective — provisional until the
 * server's dry run against the data says otherwise. This mirrors the
 * server's layering for the card the person is looking at right now, so
 * what they just added shows without a round trip, and reads the dry run's
 * result back the way the server would lay it on the next walk.
 */
import type { Annotation, ContributionValidation, DiagnoseDriver, DiagnoseEnvelope, EvidenceSource, EvidenceTier } from '../../apis';

export interface ContributionInput {
  note: string;
  dimension?: string | null;
  segment?: string | null;
  /** The driver the note was added on, when it was. */
  driver?: DiagnoseDriver | null;
}

/** What a number's grounding gives a contribution: its metric, window, perspective and slice. */
export interface ContributionTarget {
  metric: string;
  window_start: string;
  window_end: string;
  perspective: string;
  filters?: unknown[] | Record<string, unknown> | null;
  sessionId?: string | null;
}

export function targetOfEnvelope(envelope: DiagnoseEnvelope, perspective: string, sessionId?: string | null): ContributionTarget | null {
  const t = envelope?.target;
  if (!t?.metric || !t.window_start || !t.window_end) return null;
  // a root AND/OR group is one slice and travels as the group it is (#297); an unfiltered walk's {} is no filters
  const filters = Array.isArray(t.filters) ? t.filters : (t.filters && typeof t.filters === 'object' && Object.keys(t.filters).length ? t.filters : []);
  return { metric: t.metric, window_start: t.window_start, window_end: t.window_end, perspective, filters, sessionId: sessionId || null };
}

function nextDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const TIME_GRAINS = new Set(['day', 'week', 'month', 'quarter', 'year', 'hour']);

function bucketWindow(grain: string, value: unknown, start: string, end: string): { start: string; end: string } | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const opened = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(opened.getTime())) return null;
  const closed = new Date(opened);
  if (grain === 'week') closed.setUTCDate(closed.getUTCDate() + 7);
  else if (grain === 'month') closed.setUTCMonth(closed.getUTCMonth() + 1);
  else if (grain === 'quarter') closed.setUTCMonth(closed.getUTCMonth() + 3);
  else if (grain === 'year') closed.setUTCFullYear(closed.getUTCFullYear() + 1);
  else closed.setUTCDate(closed.getUTCDate() + 1);
  const bucketStart = opened.toISOString().slice(0, 10);
  closed.setUTCDate(closed.getUTCDate() - 1);
  const bucketEnd = closed.toISOString().slice(0, 10);
  const clampedStart = bucketStart < start ? start : bucketStart;
  const clampedEnd = bucketEnd > end ? end : bucketEnd;
  return clampedStart <= clampedEnd ? { start: clampedStart, end: clampedEnd } : null;
}

/** A row's column that stands for a period, not a dimension (`order_date__month`, or one the grounding names in `time_columns`). */
function isTimeBucket(g: any, column: string): boolean {
  const named = String((g?.time_columns || {})[column] || '').toLowerCase();
  if (named) return TIME_GRAINS.has(named);
  const idx = column.lastIndexOf('__');
  return idx > 0 && TIME_GRAINS.has(column.slice(idx + 2).toLowerCase());
}

/**
 * A pinned claim's grounding as a target: the review's number, over its own
 * dated window and on its own slice — the query's filters plus the row's
 * dimension values as equality filters (a null cell as IS NULL), the way
 * `claim_to_target` reads a claim for a walk. A row that carries a time
 * bucket is a period the server computes; it is not offered here.
 */
export function targetOfClaim(claim: any, perspective?: string | null, options?: { skipTimeBuckets?: boolean }): ContributionTarget | null {
  const g = claim?.grounding;
  const metric = g?.metric_qid;
  const start = g?.window?.start;
  const end = g?.window?.end;
  const p = g?.perspective || perspective;
  if (!metric || !start || !end || !p) return null;
  // the query's filters are a list of leaves, or one root AND/OR group (#297) — a group stays the group it is,
  // and a row's dimension values are ANDed onto it as conditions, never flattened to "no filters"
  const group = !Array.isArray(g?.filters) && g?.filters && typeof g.filters === 'object' && Object.keys(g.filters).length ? g.filters as Record<string, unknown> : null;
  const leaves: unknown[] = Array.isArray(g?.filters) ? [...g.filters] : [];
  const row = g?.row && typeof g.row === 'object' ? g.row : {};
  const rowLeaves: unknown[] = [];
  let targetStart = start;
  let targetEnd = end;
  for (const [dim, value] of Object.entries(row)) {
    if (isTimeBucket(g, dim)) {
      if (options?.skipTimeBuckets) {
        const named = String((g?.time_columns || {})[dim] || '').toLowerCase();
        const idx = dim.lastIndexOf('__');
        const grain = named || (idx > 0 ? dim.slice(idx + 2).toLowerCase() : '');
        const bucket = bucketWindow(grain, value, start, end);
        if (!bucket) return null;
        targetStart = bucket.start;
        targetEnd = bucket.end;
        continue;
      }
      return null;
    }
    rowLeaves.push(value == null ? { dimension: dim, operator: 'IS NULL' } : { dimension: dim, operator: '=', value });
  }
  const filters: unknown[] | Record<string, unknown> = group
    ? (rowLeaves.length ? { type: 'group', logic: 'AND', children: [group, ...rowLeaves.map((l: any) => ({ type: 'condition', ...l }))] } : group)
    : [...leaves, ...rowLeaves];
  // A claim's dates are inclusive (the query's BETWEEN); the window a contribution is bound to is half-open, so a date-only end moves to the next day — the rule `claim_to_target` applies for a walk.
  const endExclusive = targetEnd.length === 10 ? `${nextDay(targetEnd)}T00:00:00Z` : targetEnd;
  return { metric, window_start: targetStart.length === 10 ? `${targetStart}T00:00:00Z` : targetStart, window_end: endExclusive, perspective: p, filters, sessionId: null };
}

/** The annotation a contribution is stored as. */
export function contributionAnnotation(target: ContributionTarget, input: ContributionInput) {
  const note = input.note.trim();
  const dimension = input.dimension?.trim() || null;
  const segment = input.segment?.trim() || null;
  return {
    kind: 'contribution',
    ts: target.window_start,
    ts_end: target.window_end,
    title: note.length > 80 ? `${note.slice(0, 77)}…` : note,
    body: note,
    source: 'contribution',
    scope: { metrics: [target.metric], perspective: target.perspective },
    confidence: 1,
    contribution: {
      metric: input.driver?.kind === 'metric' && input.driver.id ? String(input.driver.id) : target.metric,
      ...(dimension && segment ? { dimension, segment } : {}),
      ...(input.driver ? { driver_id: String(input.driver.id ?? ''), driver_kind: input.driver.kind, driver_label: input.driver.label || input.driver.title || String(input.driver.id ?? '') } : {}),
      filters: target.filters || [],
      session_id: target.sessionId || null,
    },
  };
}

/** How a contribution's dry run reads on a card. */
export function describeValidation(v: ContributionValidation | null | undefined): string {
  if (!v || (!v.status && !v.outcome)) return 'not yet validated';
  if (v.status === 'validating') return 'validating against the data…';
  const reason = v.reason || v.outcome || '';
  return v.historical ? `${reason} (validated on another window)` : reason;
}

/** The tier a contribution earns for THIS window: the dry run's when it passed here; capped at emerging when validated elsewhere; else unconfirmed. */
export function tierOf(v: ContributionValidation | null | undefined): EvidenceTier {
  if (!v || v.status !== 'done' || v.outcome !== 'passed') return 'unconfirmed';
  const tier = (v.tier || 'unconfirmed') as EvidenceTier;
  return v.historical && tier === 'confirmed' ? 'emerging' : tier;
}

/**
 * The walk's evidence ceiling (#297), as the client can know it: the ceiling the walk wrote plus its metric drivers
 * on someone's word (capped, proposed, or over a signal-made edge not yet confirmed). A metric the walk never saw is
 * not here — the server settles that on the next walk; this mirror only keeps a dry run from lifting the ceiling
 * the card already shows.
 */
export function walkCeiling(envelope: DiagnoseEnvelope | null | undefined): { capped: Set<string>; capped_by: Record<string, unknown> | null } {
  const stored = envelope?.ceiling || null;
  const capped = new Set<string>(stored?.capped_metrics || []);
  let cappedBy: Record<string, unknown> | null = stored?.capped_by || null;
  for (const d of envelope?.drivers || []) {
    if (d.kind !== 'metric' || !d.id) continue;
    const prov: any = d.edge_provenance || {};
    const onWord = prov.method === 'signal' && d.edge_status !== 'confirmed';
    if (d.capped_by || d.proposed_by || onWord) {
      capped.add(String(d.id));
      cappedBy = cappedBy || (d.capped_by as any) || { edge: `${d.id} → ${envelope?.target?.metric || ''}`, status: d.edge_status, ...((d.proposed_by as any) || { by: prov.by, origin: prov.origin, claim_id: prov.claim_id, at: prov.at }) };
    }
  }
  return { capped, capped_by: cappedBy };
}

/** A saved contribution as the driver the server would lay onto the walk — held to the walk's ceiling (#297). */
export function contributionDriver(saved: Annotation & Record<string, any>, envelope?: DiagnoseEnvelope | null): DiagnoseDriver {
  const prov = saved.provenance || {};
  const c = prov.contribution || {};
  const v: ContributionValidation = { ...(prov.validation || {}), historical: false };
  const who = prov.asserted_by_name || prov.asserted_by || 'someone';
  let tier = tierOf(v);
  // about an upstream metric (its slice's, or the metric driver it is attached to) behind someone's word, the dry run
  // confirms the slice, never the relationship: unconfirmed, with the cap — the server's rule (_contribution_cap)
  const ceiling = walkCeiling(envelope);
  const target = envelope?.target?.metric;
  const named = [c.metric, c.driver_kind === 'metric' ? c.driver_id : null].filter((m) => typeof m === 'string' && m && m !== target) as string[];
  const cappedBy = named.some((m) => ceiling.capped.has(m)) ? ceiling.capped_by : null;
  if (cappedBy) tier = 'unconfirmed';
  const sources: EvidenceSource[] = [];
  if (v.status === 'done' && v.outcome === 'passed') {
    sources.push({ type: 'metrics', detail: v.reason || 'validated against the data', weak: tier !== 'confirmed', meta: { contribution_id: saved.id, share: v.effect?.share } });
  } else if (v.status === 'done' && v.outcome) {
    sources.push({ type: 'metrics', detail: v.reason || v.outcome, weak: true, meta: { contribution_id: saved.id, outcome: v.outcome } });
  }
  sources.push({ type: 'doc', detail: `added by ${who} · ${String(prov.asserted_at || '').slice(0, 10)}`, weak: true, meta: { contribution_id: saved.id } });
  return {
    kind: 'event', annotation_kind: 'contribution', id: saved.id, title: String(saved.body || saved.title || '').slice(0, 200), ts: saved.ts,
    tier, sources, ...(cappedBy ? { capped_by: cappedBy } : {}),
    added_by: { id: prov.asserted_by || null, name: prov.asserted_by_name || null, at: prov.asserted_at || null },
    validation: v,
    attached_to: { driver_id: c.driver_id || null, driver_kind: c.driver_kind || null, driver_label: c.driver_label || null },
  };
}

const RANK: Record<string, number> = { unconfirmed: 0, emerging: 1, confirmed: 2 };

/** Lay a saved (or re-read) contribution onto the envelope: replaces its earlier self, sits after drivers of the same or higher tier, and the note counts what the data confirmed. */
export function layerContribution(envelope: DiagnoseEnvelope, saved: Annotation & Record<string, any>): DiagnoseEnvelope {
  const d = contributionDriver(saved, envelope);
  const rest = (envelope.drivers || []).filter((x) => !(x.annotation_kind === 'contribution' && x.id === d.id));
  const rank = RANK[d.tier] ?? 0;
  let at = rest.length;
  for (let i = 0; i < rest.length; i += 1) {
    if ((RANK[rest[i].tier] ?? 0) < rank) { at = i; break; }
  }
  const drivers = [...rest.slice(0, at), d, ...rest.slice(at)];
  const contributions = drivers.filter((x) => x.annotation_kind === 'contribution').map((x) => ({
    id: String(x.id), by: x.added_by || { id: null, name: null, at: null }, tier: x.tier, validation: x.validation || {}, note: x.title || '' }));
  const confirmed = contributions.filter((c) => c.tier === 'confirmed' && !c.validation?.historical);
  const names = Array.from(new Set(confirmed.map((c) => c.by.name || c.by.id || 'someone')));
  const who = names.length <= 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const contributions_note = confirmed.length ? `${confirmed.length} validated update${confirmed.length === 1 ? '' : 's'} from ${who} included in this answer` : null;
  const other = contributions.length - confirmed.length;
  return { ...envelope, drivers, contributions, contributions_note, ...(other ? { contributions_other: other } : { contributions_other: undefined }) } as DiagnoseEnvelope;
}
